import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/SearchSelect";
import { MultiSelect } from "@/components/MultiSelect";
import { CameraCapture } from "@/components/CameraCapture";
import { toast } from "sonner";
import { Camera, Check, X, Lock, Send, Save, Loader2 } from "lucide-react";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const LABELS = [
  { key: "dx", label: "Lato destro" }, { key: "sx", label: "Lato sinistro" },
  { key: "fronte", label: "Fronte" }, { key: "retro", label: "Retro" }, { key: "interni", label: "Interni" },
];
const KEY_BY_LABEL = Object.fromEntries(LABELS.map((l) => [l.label, l.key]));
const today = () => new Date().toISOString().slice(0, 10);
const gate = (on) => (on ? "" : "pointer-events-none select-none opacity-40");

export default function CheckIn() {
  const navigate = useNavigate();
  const [commesse, setCommesse] = useState([]);
  const [members, setMembers] = useState([]);
  const [vehiclesAll, setVehiclesAll] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [locked, setLocked] = useState(null);
  const [step, setStep] = useState(1);
  const [commessaId, setCommessaId] = useState("");
  const [team, setTeam] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [eq, setEq] = useState({});
  const [conds, setConds] = useState({});
  const [camera, setCamera] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fuelPersons, setFuelPersons] = useState([]);

  useEffect(() => {
    api.get(`/fuel/day-persons?date=${today()}`).then((r) => setFuelPersons(r.data)).catch(() => {});
  }, []);

  const handleTeamChange = (next) => {
    const added = next.filter((pid) => !team.includes(pid));
    added.forEach((pid) => {
      const fp = fuelPersons.find((x) => x.employee_id === pid);
      if (fp) toast.info(`Verrà incluso il carburante di ${fp.employee_name}`);
    });
    setTeam(next);
  };

  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data)).catch(() => {});
    api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehiclesAll(r.data)).catch(() => {});
    api.get("/me/checkin-equipment").then((r) => { setEquipment(r.data); setEq((p) => Object.keys(p).length ? p : Object.fromEntries(r.data.map((e) => [e.id, { present: true, note: "" }]))); }).catch(() => {});
    api.get(`/checkins/today?date=${today()}`).then((r) => {
      const ci = r.data.checkin;
      if (!ci) return;
      if (ci.locked) { setLocked(ci); return; }
      setCommessaId(r.data.commessa_id || ""); setTeam(r.data.team || []); setVehicles(r.data.vehicles || []);
      if (ci.equipment?.length) setEq(Object.fromEntries(ci.equipment.map((e) => [e.item_id, { present: e.present, note: e.note || "" }])));
      const c = {};
      (ci.vehicle_conditions || []).forEach((vc) => { c[vc.vehicle_id] = {}; (vc.photos || []).forEach((p) => { const k = KEY_BY_LABEL[p.label]; if (k) c[vc.vehicle_id][k] = p; }); });
      setConds(c); setStep(5);
    }).catch(() => {});
  }, []);

  const advance = (s) => { if (s === 1 && !commessaId) { toast.error("Seleziona la commessa"); return; } setStep((c) => Math.max(c, s + 1)); };
  const setPhoto = (vehId, key, photo) => setConds((p) => ({ ...p, [vehId]: { ...(p[vehId] || {}), [key]: photo } }));
  const vehName = (id) => { const v = vehiclesAll.find((x) => x.id === id); return v ? (v.plate ? `${v.name} (${v.plate})` : v.name) : id; };

  const requestUnlock = async () => {
    try { await api.post("/checkins/request-unlock", { date: today() }); toast.success("Richiesta di sblocco inviata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const buildPayload = (draft) => ({
    commessa_id: commessaId, date: today(), team, vehicles, draft,
    equipment: equipment.map((e) => ({ item_id: e.id, name: e.name, present: eq[e.id]?.present ?? true, note: eq[e.id]?.note || "" })),
    vehicle_conditions: vehicles.map((vid) => ({ vehicle_id: vid, name: vehName(vid), photos: LABELS.map((l) => ({ label: l.label, ...(conds[vid]?.[l.key] || {}) })).filter((p) => p.path) })),
  });

  const save = async (draft) => {
    if (!commessaId) { toast.error("Seleziona la commessa"); return; }
    if (!draft) {
      if (vehicles.length === 0) { toast.error("Seleziona almeno un mezzo"); return; }
      for (const vid of vehicles) {
        const got = conds[vid] || {};
        const miss = LABELS.filter((l) => !got[l.key]);
        if (miss.length) { toast.error(`Foto mancanti per ${vehName(vid)}: ${miss.map((m) => m.label).join(", ")}`); return; }
      }
    }
    setSaving(true);
    try {
      let loc = null;
      if (!draft) loc = await new Promise((res) => { if (!navigator.geolocation) return res(null); navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }), () => res(null), { enableHighAccuracy: true, timeout: 8000 }); });
      await api.post("/checkins", { ...buildPayload(draft), location: loc });
      toast.success(draft ? "Bozza check-in salvata" : "Check-in trasmesso");
      navigate("/miei-rapportini");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (locked) {
    return (
      <div className="mx-auto max-w-2xl">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Check-in cantiere</h1>
        <Card className="mt-6 rounded-lg border-amber-200 bg-amber-50 p-6 text-center shadow-sm" data-testid="checkin-locked">
          <Lock className="mx-auto h-8 w-8 text-amber-600" />
          <p className="mt-3 font-heading font-bold text-slate-900">Check-in già trasmesso oggi</p>
          {locked.unlock_requested ? <p className="mt-3 text-sm font-medium text-amber-700">Richiesta di sblocco in attesa.</p>
            : <Button onClick={requestUnlock} data-testid="checkin-request-unlock" className="mt-4 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Richiedi sblocco</Button>}
        </Card>
      </div>
    );
  }

  const AvantiBtn = ({ s }) => step === s ? <div className="mt-4 flex justify-end"><Button onClick={() => advance(s)} data-testid={`checkin-next-${s}`} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div> : null;

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Check-in cantiere</h1>
          <p className="mt-1 text-sm text-slate-500">Compila passo dopo passo; puoi salvare la bozza e riprendere più tardi.</p>
        </div>
        <Button variant="outline" onClick={() => save(true)} disabled={saving} data-testid="checkin-save-draft" className="shrink-0 rounded-md">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva bozza</Button>
      </div>

      <Card className="mb-4 rounded-lg border-slate-200 p-5 shadow-sm">
        <Label className="text-xs text-slate-500">Commessa</Label>
        <div className="mt-1.5"><SearchSelect testid="checkin-commessa" options={commesse.map((c) => ({ value: c.id, label: `${c.name}${c.code ? ` · ${c.code}` : ""}` }))} value={commessaId} onChange={setCommessaId} emptyLabel="Seleziona commessa" placeholder="Cerca commessa..." /></div>
        <AvantiBtn s={1} />
      </Card>

      <Card className={`mb-4 rounded-lg border-slate-200 p-5 shadow-sm ${gate(step >= 2)}`}>
        <Label className="text-xs text-slate-500">Persone presenti</Label>
        <div className="mt-1.5"><MultiSelect testid="checkin-team" options={members.map((m) => ({ value: m.id, label: m.name }))} value={team} onChange={handleTeamChange} allLabel="Nessuna persona" placeholder="Cerca persona..." /></div>
        <AvantiBtn s={2} />
      </Card>

      <Card className={`mb-4 rounded-lg border-slate-200 p-5 shadow-sm ${gate(step >= 3)}`}>
        <Label className="text-xs text-slate-500">Mezzi presenti</Label>
        <div className="mt-1.5"><MultiSelect testid="checkin-vehicles" options={vehiclesAll.map((v) => ({ value: v.id, label: v.plate ? `${v.name} (${v.plate})` : v.name }))} value={vehicles} onChange={setVehicles} allLabel="Nessun mezzo" placeholder="Cerca mezzo..." /></div>
        <AvantiBtn s={3} />
      </Card>

      <Card className={`mb-4 rounded-lg border-slate-200 p-5 shadow-sm ${gate(step >= 4)}`}>
        <h2 className="mb-3 font-heading font-bold text-slate-900">Dispositivi e attrezzature</h2>
        {equipment.length === 0 ? <p className="text-sm text-slate-400">Nessuna attrezzatura configurata.</p> : (
          <div className="space-y-2">
            {equipment.map((e) => { const st = eq[e.id] || { present: true, note: "" }; return (
              <div key={e.id} className="rounded-md border border-slate-100 p-2.5" data-testid={`checkin-eq-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-800">{e.name}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant={st.present ? "default" : "outline"} onClick={() => setEq({ ...eq, [e.id]: { ...st, present: true } })} className={`h-7 rounded-md ${st.present ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}><Check className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={!st.present ? "default" : "outline"} onClick={() => setEq({ ...eq, [e.id]: { ...st, present: false } })} className={`h-7 rounded-md ${!st.present ? "bg-rose-600 hover:bg-rose-700" : ""}`}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <Input value={st.note} onChange={(ev) => setEq({ ...eq, [e.id]: { ...st, note: ev.target.value } })} placeholder="Nota (opzionale)" className="mt-2 h-8 text-sm" />
              </div>
            ); })}
          </div>
        )}
        <AvantiBtn s={4} />
      </Card>

      <Card className={`mb-4 rounded-lg border-slate-200 p-5 shadow-sm ${gate(step >= 5)}`}>
        <h2 className="mb-1 font-heading font-bold text-slate-900">Stato mezzi (5 foto per mezzo)</h2>
        <p className="mb-3 text-xs text-slate-500">Solo fotocamera; GPS, data e ora vengono impressi automaticamente.</p>
        {vehicles.length === 0 ? <p className="text-sm text-slate-400">Nessun mezzo selezionato.</p> : (
          <div className="space-y-4">
            {vehicles.map((vid) => (
              <div key={vid} className="rounded-lg border border-slate-200 p-3" data-testid={`checkin-veh-${vid}`}>
                <p className="mb-2 text-sm font-semibold text-slate-800">{vehName(vid)}</p>
                <div className="grid grid-cols-3 gap-2">
                  {LABELS.map((l) => { const ph = conds[vid]?.[l.key]; return (
                    <button key={l.key} type="button" onClick={() => setCamera({ vehId: vid, key: l.key, label: l.label })} data-testid={`checkin-photo-${vid}-${l.key}`} className="relative aspect-square overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 text-[11px] text-slate-500 hover:border-[#002FA7]">
                      {ph ? <img src={FILE_BASE + ph.path} alt={l.label} className="h-full w-full object-cover" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-1"><Camera className="h-4 w-4" />{l.label}</span>}
                      {ph && <span className="absolute bottom-0 left-0 right-0 bg-emerald-600/90 py-0.5 text-center text-[10px] text-white">{l.label} ✓</span>}
                    </button>
                  ); })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {step >= 5 && <Button onClick={() => save(false)} disabled={saving} data-testid="checkin-submit" className="w-full rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Send className="mr-2 h-4 w-4" />Conferma check-in</Button>}

      <CameraCapture open={!!camera} label={camera?.label} onClose={() => setCamera(null)} onCapture={(photo) => setPhoto(camera.vehId, camera.key, photo)} />
    </div>
  );
}
